export function formatResultValue(r) {
  const open = r.open_panna || "***";
  const jodi = r.jodi || "**";
  const close = r.close_panna || "***";
  if (!r.open_panna && !r.jodi && !r.close_panna) return "Waiting...";
  return `${open}-${jodi}-${close}`;
}

export function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

export function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    timeZone: "Asia/Kolkata",
  });
}

export function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Asia/Kolkata" });
}

export function todayISO() {
  // Current date in IST (YYYY-MM-DD)
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

// A Date whose local get* methods reflect IST wall-clock time (device-independent).
export function istNow() {
  return new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
}

// Parse a time string like "10:30 AM" / "09:40" into minutes-of-day (IST). Null if invalid.
export function timeToMinutes(s) {
  if (!s) return null;
  const m = s.match(/(\d{1,2})[:.\-](\d{2})/);
  if (!m) return null;
  let h = +m[1]; const u = s.toUpperCase();
  if (u.includes("PM") && h !== 12) h += 12;
  if (u.includes("AM") && h === 12) h = 0;
  return (h % 24) * 60 + +m[2];
}

// When true, results stay fully masked (***-**-***) until the close time passes (no early open reveal).
let _revealAfterClose = false;
export function setRevealAfterClose(v) { _revealAfterClose = !!v; }

// Reveal rules (apply to ALL markets, own + public):
//  - No result OR result is from a previous day -> ***-**-*** (never show stale/previous-day).
//  - Auto-lock ON -> masked until close time, then full.
//  - Auto-lock OFF -> before open ***; between open & close open panna + first ank; after close full.
export function revealedValue(result) {
  const v = revealToday(result);
  if (v !== "***-**-***" || !result?.prev) return v;
  const o = timeToMinutes(result.open_time);
  const now = istNow();
  // Keep the previous day's result on screen until 1 minute before today's open.
  return o != null && now.getHours() * 60 + now.getMinutes() < o - 1 ? result.prev : v;
}

function revealToday(result) {
  if (!result) return "***-**-***";
  if (result.result_date !== todayISO()) return "***-**-***";
  const o = timeToMinutes(result.open_time);
  const c = timeToMinutes(result.close_time);
  const now = istNow();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const open = result.open_panna || "***";
  const jodi = result.jodi || "**";
  const close = result.close_panna || "***";
  if (_revealAfterClose) {
    if (c != null && c >= (o ?? 0) && nowMin < c) return "***-**-***";
    return `${open}-${jodi}-${close}`;
  }
  if (o != null && nowMin < o) return "***-**-***";
  if (c != null && c >= o && nowMin < c) return `${open}-${(jodi[0] || "*")}*-***`;
  return `${open}-${jodi}-${close}`;
}

// For a chart's "today" cell: mirror the live reveal state.
// Returns {open_panna, jodi, close_panna} when the result is already "listed"
// in the live view, otherwise null (cell stays blank until it lists).
export function revealedChartParts(result, open_time, close_time) {
  if (!result) return null;
  const rv = revealedValue({ ...result, result_date: todayISO(), open_time, close_time });
  if (rv === "***-**-***") return null;
  const [open, jodi, close] = rv.split("-");
  return {
    open_panna: open === "***" ? "" : open,
    jodi: jodi === "**" ? "" : jodi,
    close_panna: close === "***" ? "" : close,
  };
}

export function monthLabel(ym) {
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// Open WhatsApp share sheet with the result text (text-only fallback).
export function shareResultOnWhatsApp(name, value) {
  const target = String.fromCodePoint(0x1F3AF);
  const text = `${target} *${name}*\nAaj ka Result: *${value}*\n\nLive Result: https://bgrmatka.com/`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
}

// Load the brand logo once for canvas drawing (served from /logo.png, same-origin so no taint).
let _logoPromise = null;
function loadLogo() {
  if (_logoPromise) return _logoPromise;
  _logoPromise = new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = "/logo.png";
    } catch (e) { console.warn("[share] image build failed:", e); resolve(null); }
  });
  return _logoPromise;
}

// Draw a branded result card to a canvas and return a PNG blob.
async function buildResultImage(name, value, timing) {
  const logo = await loadLogo();
  return new Promise((resolve) => {
    try {
      const W = 900, H = 540;
      const c = document.createElement("canvas");
      c.width = W; c.height = H;
      const ctx = c.getContext("2d");
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, "#2A0710"); g.addColorStop(1, "#5A0A14");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "#F3C24B"; ctx.lineWidth = 10; ctx.strokeRect(16, 16, W - 32, H - 32);
      ctx.textAlign = "center";
      if (logo) {
        ctx.drawImage(logo, W / 2 - 68, 28, 136, 136);
      } else {
        ctx.fillStyle = "#F3C24B"; ctx.font = "bold 44px Arial"; ctx.fillText("BGRMATKA", W / 2, 110);
      }
      ctx.fillStyle = "#FFD700"; ctx.font = "600 22px Arial"; ctx.fillText("LIVE MATKA RESULT", W / 2, 196);
      ctx.fillStyle = "#FFFFFF"; ctx.font = "bold 46px Arial"; ctx.fillText(String(name).toUpperCase(), W / 2, 266);
      ctx.fillStyle = "#FFE9A8"; ctx.font = "bold 82px monospace"; ctx.fillText(String(value), W / 2, 366);
      if (timing) { ctx.fillStyle = "#F3C24B"; ctx.font = "600 28px Arial"; ctx.fillText(timing, W / 2, 418); }
      ctx.fillStyle = "#E7C9A0"; ctx.font = "500 22px Arial";
      const dateStr = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
      ctx.fillText(dateStr, W / 2, 462);
      ctx.fillStyle = "#F3C24B"; ctx.font = "600 24px Arial"; ctx.fillText(window.location.host, W / 2, 502);
      c.toBlob((b) => resolve(b), "image/png");
    } catch (e) { console.warn("[share] image build failed:", e); resolve(null); }
  });
}

// Share the result as a nice image (Web Share API with file), fall back to text share.
export async function shareResult(name, value, timing) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const caption = `${[name, value, timing].filter(Boolean).join("  ")}\nBGRMATKA Live Result\n${origin}`;
  try {
    const blob = await buildResultImage(name, value, timing);
    if (blob && navigator.canShare) {
      const file = new File([blob], "bgrmatka-result.png", { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption });
        return;
      }
    }
    // Desktop fallback: download the image so the user can attach it.
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "bgrmatka-result.png";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    }
  } catch (e) { console.warn("[share] falling back to text share:", e); }
  shareResultOnWhatsApp(name, value, timing);
}

// Build a single branded image listing multiple market results (for bulk share).
async function buildResultsImage(title, rows) {
  const logo = await loadLogo();
  return new Promise((resolve) => {
    try {
      const W = 900;
      const headerH = 240, rowH = 90, footerH = 84;
      const H = headerH + rows.length * rowH + footerH;
      const c = document.createElement("canvas");
      c.width = W; c.height = H;
      const ctx = c.getContext("2d");
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, "#2A0710"); g.addColorStop(1, "#5A0A14");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "#F3C24B"; ctx.lineWidth = 10; ctx.strokeRect(16, 16, W - 32, H - 32);
      ctx.textAlign = "center";
      if (logo) {
        ctx.drawImage(logo, W / 2 - 58, 22, 116, 116);
      } else {
        ctx.fillStyle = "#F3C24B"; ctx.font = "bold 46px Arial"; ctx.fillText("BGRMATKA", W / 2, 96);
      }
      ctx.fillStyle = "#FFD700"; ctx.font = "600 24px Arial"; ctx.fillText(String(title).toUpperCase(), W / 2, 170);
      const dateStr = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
      ctx.fillStyle = "#E7C9A0"; ctx.font = "500 22px Arial"; ctx.fillText(dateStr, W / 2, 204);
      rows.forEach((r, i) => {
        const cy = headerH + i * rowH;
        ctx.textAlign = "left"; ctx.fillStyle = "#FFFFFF"; ctx.font = "bold 30px Arial";
        ctx.fillText(String(r.name).toUpperCase(), 60, cy + 42);
        ctx.textAlign = "right"; ctx.fillStyle = "#FFE9A8"; ctx.font = "bold 40px monospace";
        ctx.fillText(String(r.value), W - 60, cy + 44);
        ctx.strokeStyle = "rgba(243,194,75,0.25)"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(50, cy + rowH - 12); ctx.lineTo(W - 50, cy + rowH - 12); ctx.stroke();
      });
      ctx.textAlign = "center"; ctx.fillStyle = "#F3C24B"; ctx.font = "600 24px Arial";
      ctx.fillText(window.location.host, W / 2, H - 38);
      c.toBlob((b) => resolve(b), "image/png");
    } catch (e) { console.warn("[share] image build failed:", e); resolve(null); }
  });
}

// Share a set of results as one image (Web Share API), fall back to text share.
export async function shareResults(title, rows) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const lines = rows.map((r) => `${r.name}  ${r.value}`).join("\n");
  const caption = `${title}\n${lines}\nBGRMATKA Live Result\n${origin}`;
  try {
    const blob = await buildResultsImage(title, rows);
    if (blob && navigator.canShare) {
      const file = new File([blob], "bgrmatka-results.png", { type: "image/png" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption });
        return;
      }
    }
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "bgrmatka-results.png";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    }
  } catch (e) { console.warn("[share] falling back to text share:", e); }
  window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank", "noopener");
}

// Short pleasant beep using the Web Audio API (played when a result is declared).
let _audioCtx = null;
export function playResultBeep() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    if (!_audioCtx) _audioCtx = new AC();
    if (_audioCtx.state === "suspended") _audioCtx.resume();
    const t = _audioCtx.currentTime;
    [880, 1320].forEach((freq, i) => {
      const osc = _audioCtx.createOscillator();
      const gain = _audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = t + i * 0.18;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
      osc.connect(gain); gain.connect(_audioCtx.destination);
      osc.start(start); osc.stop(start + 0.18);
    });
  } catch (e) { /* audio not available */ }
}

const RED_JODIS = new Set(["00", "11", "22", "33", "44", "55", "66", "77", "88", "99", "05", "16", "27", "38", "49", "50", "61", "72", "83", "94"]);
export const isRedJodi = (jd) => RED_JODIS.has(jd || "");

export const marketSlug = (name = "") => name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
export const chartPath = (name, type = "panel") => `/${type === "jodi" ? "jodi" : "panel"}-chart-record/${marketSlug(name)}`;
